import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

export const USER_PROFILE_KEY = 'v3-user-profile'
export const USER_PROFILE_SCOPES_KEY = 'user-profile-scopes'

type OrganizationProfileResponse = {
  id: string
  name: string
  imageUrl?: string
}

type UserScopeProfileResponse = {
  id: string
  role: string
  dateCreated: Date
  organization: OrganizationProfileResponse
}

type UserProfileBase = {
  fullName: string
  firstAddressLine: string
  secondAddressLine: string
  zipcode: string
  city: string
  stateOrProvince: string
  country?: string
  phoneNumber: string
  isSmsNotificationsEnabled?: boolean
  isEmailNotificationsEnabled?: boolean
}

type UserProfileResponse = UserProfileBase & {
  avatarUrl: string | null
}
type UserProfileRequest = Omit<UserProfileBase, 'isEmailNotificationsEnabled'>

type UserProfileOTPVerificationRequest = {
  code: string
}

export function useChangeUserScope() {
  return useMutation({
    mutationFn: async (scopeId: string) => {
      const { data } = await apiClient.post(`/int/v3/users/me/scopes/${scopeId}`)
      return data
    },
  })
}

export function useGetMineScopes() {
  return useQuery({
    queryKey: [USER_PROFILE_SCOPES_KEY],
    queryFn: async () => (await apiClient.get<UserScopeProfileResponse[]>(`/int/v3/users/me/scopes`)).data,
    refetchOnWindowFocus: false,
  })
}

export function useGetMine() {
  return useQuery({
    queryKey: [USER_PROFILE_KEY],
    queryFn: async () => (await apiClient.get<UserProfileResponse>('/int/v3/users/me/profile')).data,
    refetchOnWindowFocus: false,
  })
}

// The API uses PUT for both the initial profile creation and later edits (full
// replace semantics either way), so create and edit share this implementation.
export function useCreateMine(onSuccess?: () => void, onError?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: UserProfileRequest) => {
      const { data } = await apiClient.put<UserProfileResponse>('/int/v3/users/me/profile', values)
      return data
    },
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: [USER_PROFILE_KEY] })
      queryClient.setQueryData([USER_PROFILE_KEY], data)
      onSuccess?.()
    },
    onError: onError,
  })
}

export const useEditMine = useCreateMine

export function useSendOTP(onSuccess?: () => void, onError?: () => void) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/int/v3/users/me/profile/sms-opt-in')
      return data
    },
    onSuccess: onSuccess,
    onError: onError,
  })
}

export function useVerifyOTP(onSuccess?: () => void, onError?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (values: UserProfileOTPVerificationRequest) => {
      const { data } = await apiClient.post('/int/v3/users/me/profile/sms-opt-in/verification', values)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USER_PROFILE_KEY] })
      onSuccess?.()
    },
    onError: onError,
  })
}
